import cx from "classnames";

import Box from "ds/components/Box";
import Tooltip from "ds/components/Tooltip";
import { TooltipReferenceProps } from "ds/components/Tooltip/types";
import { formatDate } from "utils/date";
import { unixTimestampToMs } from "utils/time";

import styles from "./styles.module.css";

enum TerraformProviderVersionVerificationStatus {
  Pending = "PENDING",
  Valid = "VALID",
  Failed = "FAILED",
}

type TerraformProviderVersionVerificationStatusConfig = {
  variant: "green" | "gray" | "red";
  text: string;
};

/** @public (Knip ignored - used in the file and exported for stories) */
export const PROVIDER_VERSION_VERIFICATION_STATUSES: Record<
  TerraformProviderVersionVerificationStatus,
  TerraformProviderVersionVerificationStatusConfig
> = {
  [TerraformProviderVersionVerificationStatus.Pending]: { variant: "gray", text: "pending" },
  [TerraformProviderVersionVerificationStatus.Valid]: { variant: "green", text: "valid" },
  [TerraformProviderVersionVerificationStatus.Failed]: { variant: "red", text: "failed" },
};

type ProviderVersionVerification = {
  verificationFailure?: string | null;
  verifiedAt?: number | null;
};

/** @public (Knip ignored - used in the file and exported for stories) */
export const getProviderVersionVerificationStatus = ({
  verificationFailure,
  verifiedAt,
}: ProviderVersionVerification): TerraformProviderVersionVerificationStatus => {
  if (verificationFailure) {
    return TerraformProviderVersionVerificationStatus.Failed;
  }

  if (verifiedAt) {
    return TerraformProviderVersionVerificationStatus.Valid;
  }

  return TerraformProviderVersionVerificationStatus.Pending;
};

type TerraformProviderVersionVerificationStatusBadgeProps = ProviderVersionVerification & {
  className?: string;
};

const TerraformProviderVersionVerificationStatusBadge = ({
  verificationFailure,
  verifiedAt,
  className,
}: TerraformProviderVersionVerificationStatusBadgeProps) => {
  const status = getProviderVersionVerificationStatus({ verificationFailure, verifiedAt });
  const { variant, text } = PROVIDER_VERSION_VERIFICATION_STATUSES[status];

  let tooltip: string | undefined;
  if (status === TerraformProviderVersionVerificationStatus.Valid && verifiedAt) {
    tooltip = `GPG signature and checksums successfully verified on ${formatDate({
      date: unixTimestampToMs(verifiedAt),
    })}.`;
  } else if (status === TerraformProviderVersionVerificationStatus.Failed) {
    tooltip = `Failed to verify GPG signature and checksum: ${verificationFailure}`;
  }

  return (
    <Tooltip
      active={!!tooltip}
      on={(tooltipProps: TooltipReferenceProps) => (
        <Box
          {...tooltipProps}
          className={cx(styles.badge, styles[variant], tooltipProps.className, className)}
        >
          <span className={cx(styles.segment, styles.prefix)}>Verified</span>
          <span className={cx(styles.segment, styles.status)}>{text}</span>
        </Box>
      )}
    >
      {tooltip}
    </Tooltip>
  );
};

export default TerraformProviderVersionVerificationStatusBadge;